import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, FolderTree, Search, Trash2, Pencil, FilePlus2, FolderOpen, UserPlus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  normalizeRole,
  canAccessManagePatterns,
  canCreateOrEditClients,
  canDeleteClients,
  canDeleteHistoricalPatterns,
  canSavePatternVersion,
} from '@/lib/rbac';
import { getGarmentConfig, getSupportedGeometryVersions, resolveGeometryVersion } from '@/lib/drafting';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const GARMENT_OPTIONS = ['trouser', 'shirt', 'jacket', 'waistcoat', 'overcoat'];
const GARMENT_PREFIX_MAP = {
  trouser: 'T',
  shirt: 'S',
  jacket: 'J',
  waistcoat: 'W',
  overcoat: 'O',
};

const JACKET_BELLY_PROFILE_OPTIONS = [
  { value: 'slim', label: 'Slim' },
  { value: 'normal', label: 'Normal' },
  { value: 'slight_belly', label: 'Slight Belly' },
  { value: 'big_belly', label: 'Big Belly' },
  { value: 'extreme_belly', label: 'Extreme Belly' },
];

const JACKET_POSTURE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'stoop', label: 'Stoop' },
  { value: 'sway', label: 'Sway' },
];

const buildPatternNumber = (garmentType, clientNumber, versionNumber) => {
  const prefix = GARMENT_PREFIX_MAP[garmentType] || 'T';
  return `${prefix}${String(clientNumber || '').toUpperCase()}-${versionNumber}`;
};

function ColumnHeader({ title, subtitle }) {
  return (
    <div className="px-4 py-3 border-b bg-muted/30">
      <h2 className="text-sm font-semibold tracking-wide">{title}</h2>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
  );
}

export default function ManagePatterns({ token, user, onBack, onOpenPattern, onStartPatternDraft }) {
  const [clients, setClients] = useState([]);
  const [garments, setGarments] = useState([]);
  const [patterns, setPatterns] = useState([]);

  const [clientSearch, setClientSearch] = useState('');
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  const [garmentSearch, setGarmentSearch] = useState('');
  const [patternSearch, setPatternSearch] = useState('');

  const [clientPage, setClientPage] = useState(1);
  const [garmentPage, setGarmentPage] = useState(1);
  const [patternPage, setPatternPage] = useState(1);

  const [clientTotal, setClientTotal] = useState(0);
  const [garmentTotal, setGarmentTotal] = useState(0);
  const [patternTotal, setPatternTotal] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const [selectedPatternIds, setSelectedPatternIds] = useState([]);

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingGarments, setLoadingGarments] = useState(false);
  const [loadingPatterns, setLoadingPatterns] = useState(false);

  const [actionMessage, setActionMessage] = useState('');

  const [isClientEditDialogOpen, setIsClientEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientEditForm, setClientEditForm] = useState({
    client_number: '',
    client_name: '',
  });
  const [clientEditError, setClientEditError] = useState('');
  const [isClientEditSaving, setIsClientEditSaving] = useState(false);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [editForm, setEditForm] = useState({
    pattern_number: '',
    garment_type: '',
    notes: '',
  });
  const [isEditSaving, setIsEditSaving] = useState(false);

  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ client_number: '', client_name: '' });
  const [newClientError, setNewClientError] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);
  const [draftMode, setDraftMode] = useState('new_garment');
  const [draftSourcePattern, setDraftSourcePattern] = useState(null);
  const [draftForm, setDraftForm] = useState({
    garment_type: 'trouser',
    version_number: '1',
    pattern_number: '',
    geometry_version: '',
    bellyProfile: 'normal',
    posture: 'normal',
    notes: '',
  });
  const [draftError, setDraftError] = useState('');
  const [isDraftLoadingVersion, setIsDraftLoadingVersion] = useState(false);
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const [isSourcePatternDialogOpen, setIsSourcePatternDialogOpen] = useState(false);
  const [sourcePatternTargetGarment, setSourcePatternTargetGarment] = useState('trouser');
  const [sourcePatternSearchText, setSourcePatternSearchText] = useState('');
  const [sourcePatternOptions, setSourcePatternOptions] = useState([]);
  const [selectedSourcePatternForDuplicate, setSelectedSourcePatternForDuplicate] = useState(null);
  const [sourcePatternDialogError, setSourcePatternDialogError] = useState('');
  const [isSourcePatternLoading, setIsSourcePatternLoading] = useState(false);
  const clientRequestIdRef = useRef(0);

  const normalizeClientKey = useCallback((value) => String(value || '').trim().toLowerCase(), []);

  const role = normalizeRole(user?.role);
  const canManage = canAccessManagePatterns(role);
  const canCreateOrEditClientRecords = canCreateOrEditClients(role);
  const canDeleteClientRecords = canDeleteClients(role);
  const canDeletePatternRecords = canDeleteHistoricalPatterns(role);
  const canCreatePatternDrafts = canSavePatternVersion(role);
  const canEditPatternRecords = canSavePatternVersion(role);
  const pageSize = 20;

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  const isExactDuplicateDraftMode = draftMode === 'revision' || draftMode === 'cross_client_duplicate';

  useEffect(() => {
    clientRequestIdRef.current += 1;
    const timeoutId = window.setTimeout(() => {
      setClientPage(1);
      setDebouncedClientSearch(clientSearch.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [clientSearch]);

  const fetchClients = useCallback(async () => {
    if (!canManage) return;
    const requestId = clientRequestIdRef.current + 1;
    clientRequestIdRef.current = requestId;
    setLoadingClients(true);
    try {
      const params = new URLSearchParams({
        search: debouncedClientSearch,
        page: String(clientPage),
        page_size: String(pageSize),
      });
      const response = await fetch(`${API_BASE}/api/patterns/manage/clients?${params}`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('Failed to fetch clients');
      const data = await response.json();
      if (requestId !== clientRequestIdRef.current) return;
      setClients(data.items || []);
      setClientTotal(data.total || 0);
    } catch (err) {
      if (requestId !== clientRequestIdRef.current) return;
      setActionMessage(err.message);
    } finally {
      if (requestId === clientRequestIdRef.current) setLoadingClients(false);
    }
  }, [authHeaders, canManage, clientPage, debouncedClientSearch]);

  const fetchGarments = useCallback(async () => {
    if (!canManage || !selectedClient?.client_number) {
      setGarments([]);
      setGarmentTotal(0);
      return;
    }

    setLoadingGarments(true);
    try {
      const params = new URLSearchParams({
        client_number: selectedClient.client_number,
        search: garmentSearch,
        page: String(garmentPage),
        page_size: String(pageSize),
      });
      const response = await fetch(`${API_BASE}/api/patterns/manage/garments?${params}`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('Failed to fetch garments');
      const data = await response.json();
      setGarments(data.items || []);
      setGarmentTotal(data.total || 0);
    } catch (err) {
      setActionMessage(err.message);
    } finally {
      setLoadingGarments(false);
    }
  }, [authHeaders, canManage, garmentPage, garmentSearch, selectedClient]);

  const fetchPatterns = useCallback(async () => {
    if (!canManage || !selectedClient?.client_number) {
      setPatterns([]);
      setPatternTotal(0);
      return;
    }

    setLoadingPatterns(true);
    try {
      const params = new URLSearchParams({
        client_number: selectedClient.client_number,
        garment_type: selectedGarment?.garment_type || '',
        search: patternSearch,
        page: String(patternPage),
        page_size: String(pageSize),
      });
      const response = await fetch(`${API_BASE}/api/patterns/manage/list?${params}`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('Failed to fetch patterns');
      const data = await response.json();
      setPatterns(data.items || []);
      setPatternTotal(data.total || 0);
    } catch (err) {
      setActionMessage(err.message);
    } finally {
      setLoadingPatterns(false);
    }
  }, [authHeaders, canManage, patternPage, patternSearch, selectedClient, selectedGarment]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchGarments();
  }, [fetchGarments]);

  useEffect(() => {
    fetchPatterns();
  }, [fetchPatterns]);

  const refreshAllColumns = async () => {
    await Promise.all([fetchClients(), fetchGarments(), fetchPatterns()]);
  };

  const fetchNextVersion = useCallback(async (clientNumber, garmentType) => {
    const params = new URLSearchParams({
      client_number: String(clientNumber || '').toUpperCase(),
      garment_type: garmentType,
    });
    const response = await fetch(`${API_BASE}/api/patterns/manage/next-version?${params}`, {
      headers: authHeaders,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to resolve next version');
    return data;
  }, [authHeaders]);

  const checkPatternExists = useCallback(async (patternNumber) => {
    const response = await fetch(`${API_BASE}/api/patterns/exists/${patternNumber}`, {
      headers: authHeaders,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to validate pattern number');
    return Boolean(data.exists);
  }, [authHeaders]);

  const openSourcePatternDuplicateDialog = useCallback(() => {
    if (!canCreatePatternDrafts) {
      setActionMessage('You do not have permission to create pattern drafts.');
      return;
    }

    if (!selectedClient) {
      setActionMessage('Select a target client first.');
      return;
    }

    setSourcePatternTargetGarment(selectedGarment?.garment_type || 'trouser');
    setSourcePatternSearchText('');
    setSourcePatternOptions([]);
    setSelectedSourcePatternForDuplicate(null);
    setSourcePatternDialogError('');
    setIsSourcePatternDialogOpen(true);
  }, [canCreatePatternDrafts, selectedClient, selectedGarment]);

  const openDraftDialog = useCallback(async (mode, options = {}) => {
    if (!canCreatePatternDrafts) {
      setActionMessage('You do not have permission to create pattern drafts.');
      return;
    }

    if (!selectedClient) {
      setActionMessage('Select a client first.');
      return;
    }

    const sourcePattern = options.sourcePattern || null;
    const forcedGarment = options.forcedGarment || sourcePattern?.garment_type || selectedGarment?.garment_type || 'trouser';
    const safeVersion = '1';

    setDraftMode(mode);
    setDraftSourcePattern(sourcePattern);
    setDraftError('');
    setDraftForm({
      garment_type: forcedGarment,
      version_number: safeVersion,
      pattern_number: buildPatternNumber(forcedGarment, selectedClient.client_number, safeVersion),
      geometry_version: sourcePattern
        ? resolveGeometryVersion(forcedGarment, sourcePattern)
        : (getGarmentConfig(forcedGarment)?.geometryVersion || ''),
      bellyProfile: forcedGarment === 'jacket'
        ? String(sourcePattern?.measurements?.bellyProfile || 'normal').toLowerCase()
        : 'normal',
      posture: forcedGarment === 'jacket'
        ? String(sourcePattern?.measurements?.posture || 'normal').toLowerCase()
        : 'normal',
      notes: mode === 'cross_client_duplicate' ? '' : String(sourcePattern?.notes || ''),
    });
    setIsDraftDialogOpen(true);

    setIsDraftLoadingVersion(true);
    try {
      const next = await fetchNextVersion(selectedClient.client_number, forcedGarment);
      const resolvedVersion = String(next.next_version_number || 1);
      setDraftForm((prev) => ({
        ...prev,
        garment_type: forcedGarment,
        version_number: resolvedVersion,
        pattern_number: String(next.next_pattern_number || buildPatternNumber(forcedGarment, selectedClient.client_number, resolvedVersion)).toUpperCase(),
      }));
    } catch (err) {
      setDraftError(err.message);
    } finally {
      setIsDraftLoadingVersion(false);
    }
  }, [canCreatePatternDrafts, fetchNextVersion, selectedClient, selectedGarment]);

  const handleDuplicatePattern = useCallback((pattern) => {
    openDraftDialog('revision', { sourcePattern: pattern, forcedGarment: pattern?.garment_type });
  }, [openDraftDialog]);

  const handleSourcePatternDuplicateContinue = useCallback(async () => {
    if (!selectedSourcePatternForDuplicate) {
      setSourcePatternDialogError('Select a source pattern to continue.');
      return;
    }

    setIsSourcePatternDialogOpen(false);
    await openDraftDialog('cross_client_duplicate', {
      sourcePattern: selectedSourcePatternForDuplicate,
      forcedGarment: sourcePatternTargetGarment,
    });
  }, [openDraftDialog, selectedSourcePatternForDuplicate, sourcePatternTargetGarment]);

  const handleCreateClient = async () => {
    if (!canCreateOrEditClientRecords) {
      setNewClientError('You do not have permission to create clients.');
      return;
    }

    setNewClientError('');
    setIsCreatingClient(true);
    try {
      const payload = {
        client_number: String(newClientForm.client_number || '').toUpperCase().trim(),
        client_name: String(newClientForm.client_name || '').trim(),
      };

      if (!payload.client_number || !payload.client_name) {
        throw new Error('Client number and client name are required.');
      }

      const response = await fetch(`${API_BASE}/api/patterns/manage/client`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to create client');

      setActionMessage(`Client #${data.client_number} created.`);
      setSelectedClient(data);
      setSelectedGarment(null);
      setSelectedPatternIds([]);
      setClientPage(1);
      setGarmentPage(1);
      setPatternPage(1);
      setIsNewClientDialogOpen(false);
      setNewClientForm({ client_number: '', client_name: '' });
      await refreshAllColumns();
    } catch (err) {
      setNewClientError(err.message);
    } finally {
      setIsCreatingClient(false);
    }
  };

  const openClientEditDialog = (client) => {
    if (!canCreateOrEditClientRecords) {
      setActionMessage('You do not have permission to edit clients.');
      return;
    }

    setEditingClient(client);
    setClientEditError('');
    setClientEditForm({
      client_number: String(client?.client_number || '').toUpperCase(),
      client_name: String(client?.client_name || ''),
    });
    setIsClientEditDialogOpen(true);
  };

  const handleClientEditSave = async () => {
    if (!editingClient) return;
    if (!canCreateOrEditClientRecords) {
      setClientEditError('You do not have permission to edit clients.');
      return;
    }

    setClientEditError('');
    setIsClientEditSaving(true);
    try {
      const payload = {
        client_number: String(clientEditForm.client_number || '').toUpperCase().trim(),
        client_name: String(clientEditForm.client_name || '').trim(),
      };

      if (!payload.client_number || !payload.client_name) {
        throw new Error('Client number and name are required.');
      }

      const response = await fetch(`${API_BASE}/api/patterns/manage/client/${encodeURIComponent(editingClient.client_number)}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update client');

      setActionMessage(`Client #${data.client_number} updated.`);
      if (selectedClient?.client_number === editingClient.client_number) {
        setSelectedClient((prev) => ({
          ...prev,
          client_number: data.client_number,
          client_name: data.client_name,
          pattern_count: data.pattern_count,
        }));
        setSelectedGarment(null);
        setSelectedPatternIds([]);
      }
      setIsClientEditDialogOpen(false);
      setEditingClient(null);
      await refreshAllColumns();
    } catch (err) {
      setClientEditError(err.message);
    } finally {
      setIsClientEditSaving(false);
    }
  };

  const handleClientDelete = async (client) => {
    if (!client) return;

    if (!canDeleteClientRecords) {
      setActionMessage('Only owners and admins can delete clients.');
      return;
    }

    if (Number(client.pattern_count || 0) > 0) {
      setActionMessage('Client deletion disabled: garments/patterns exist for this client.');
      return;
    }

    if (!window.confirm(`Delete client #${client.client_number} permanently?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/patterns/manage/client/${encodeURIComponent(client.client_number)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to delete client');

      setActionMessage(`Client #${client.client_number} deleted.`);
      if (selectedClient?.client_number === client.client_number) {
        setSelectedClient(null);
        setSelectedGarment(null);
        setSelectedPatternIds([]);
      }
      await refreshAllColumns();
    } catch (err) {
      setActionMessage(err.message);
    }
  };

  const handleDraftSubmit = async () => {
    if (!canCreatePatternDrafts) {
      setDraftError('You do not have permission to create drafts.');
      return;
    }

    if (!selectedClient) {
      setDraftError('Select a client first.');
      return;
    }

    setDraftError('');
    setIsDraftSubmitting(true);

    try {
      const enteredPatternNumber = String(draftForm.pattern_number || '').trim().toUpperCase();
      if (!enteredPatternNumber) {
        throw new Error('Pattern number is required.');
      }

      const versionMatch = enteredPatternNumber.match(/-(\d+)$/);
      const versionNumber = Number(versionMatch?.[1] || draftForm.version_number);
      if (!Number.isInteger(versionNumber) || versionNumber < 1) {
        throw new Error('Pattern number must end with a version number greater than 0.');
      }

      const garmentType = draftForm.garment_type;
      const patternNumber = enteredPatternNumber;

      const exists = await checkPatternExists(patternNumber);
      if (exists) {
        throw new Error(`Pattern ${patternNumber} already exists. Please use another pattern number.`);
      }

      if (typeof onStartPatternDraft === 'function') {
        const inheritedSourceGeometry = draftMode === 'revision'
          ? resolveGeometryVersion(garmentType, draftSourcePattern || {})
          : null;
        const draftGeometryVersion = (draftForm.geometry_version || '').trim()
          || inheritedSourceGeometry
          || getGarmentConfig(garmentType)?.geometryVersion
          || '';

        onStartPatternDraft({
          client_number: String(selectedClient.client_number || '').toUpperCase(),
          client_name: selectedClient.client_name,
          garment_type: garmentType,
          version_number: String(versionNumber),
          pattern_number: patternNumber,
          geometry_version: draftGeometryVersion,
          sourcePattern: isExactDuplicateDraftMode ? draftSourcePattern : null,
          duplicateExact: isExactDuplicateDraftMode,
          bellyProfile: garmentType === 'jacket' ? draftForm.bellyProfile : undefined,
          posture: garmentType === 'jacket' ? draftForm.posture : undefined,
          notes: String(draftForm.notes || '').trim() || null,
        });
      }

      setActionMessage(isExactDuplicateDraftMode
        ? `Duplicate opened: ${patternNumber}`
        : `Draft opened: ${patternNumber}`);
      setIsDraftDialogOpen(false);
      setDraftSourcePattern(null);
    } catch (err) {
      setDraftError(err.message);
    } finally {
      setIsDraftSubmitting(false);
    }
  };

  const handleDraftGarmentChange = async (garmentType) => {
    if (!selectedClient) return;

    setDraftForm((prev) => ({
      ...prev,
      garment_type: garmentType,
      version_number: '1',
      pattern_number: buildPatternNumber(garmentType, selectedClient.client_number, 1),
      geometry_version: getGarmentConfig(garmentType)?.geometryVersion || '',
      bellyProfile: garmentType === 'jacket' ? 'normal' : prev.bellyProfile,
      posture: garmentType === 'jacket' ? 'normal' : prev.posture,
      notes: '',
    }));

    setIsDraftLoadingVersion(true);
    setDraftError('');
    try {
      const next = await fetchNextVersion(selectedClient.client_number, garmentType);
      const resolvedVersion = String(next.next_version_number || 1);
      setDraftForm((prev) => ({
        ...prev,
        garment_type: garmentType,
        version_number: resolvedVersion,
        pattern_number: String(next.next_pattern_number || buildPatternNumber(garmentType, selectedClient.client_number, resolvedVersion)).toUpperCase(),
      }));
    } catch (err) {
      setDraftError(err.message);
    } finally {
      setIsDraftLoadingVersion(false);
    }
  };

  useEffect(() => {
    if (!isSourcePatternDialogOpen || !sourcePatternTargetGarment) return;

    let cancelled = false;
    const loadSourcePatterns = async () => {
      setIsSourcePatternLoading(true);
      setSourcePatternDialogError('');
      try {
        const params = new URLSearchParams({
          garment_type: sourcePatternTargetGarment,
        });
        const response = await fetch(`${API_BASE}/api/patterns?${params}`, {
          headers: authHeaders,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Failed to fetch source patterns');
        if (cancelled) return;
        const normalizedTargetClient = String(selectedClient?.client_number || '').trim().toUpperCase();
        setSourcePatternOptions((Array.isArray(data) ? data : []).filter((pattern) => (
          String(pattern?.client_number || '').trim().toUpperCase() !== normalizedTargetClient
        )));
      } catch (err) {
        if (!cancelled) {
          setSourcePatternOptions([]);
          setSourcePatternDialogError(err.message || 'Failed to fetch source patterns');
        }
      } finally {
        if (!cancelled) setIsSourcePatternLoading(false);
      }
    };

    loadSourcePatterns();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, isSourcePatternDialogOpen, selectedClient?.client_number, sourcePatternTargetGarment]);

  const filteredSourcePatternOptions = useMemo(() => {
    const query = sourcePatternSearchText.trim().toLowerCase();
    const filtered = (sourcePatternOptions || []).filter((pattern) => {
      if (!query) return true;
      return [
        pattern?.pattern_number,
        pattern?.client_number,
        pattern?.client_name,
        pattern?.notes,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
    return filtered.slice(0, 30);
  }, [sourcePatternOptions, sourcePatternSearchText]);

  const handleSingleDelete = async (patternId) => {
    if (!canDeletePatternRecords) {
      setActionMessage('Only owners and admins can delete historical patterns.');
      return;
    }

    if (!window.confirm('Delete this pattern permanently?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/patterns/${patternId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to delete pattern');

      setActionMessage('Pattern deleted successfully.');
      setSelectedPatternIds((prev) => prev.filter((id) => id !== patternId));
      await refreshAllColumns();
    } catch (err) {
      setActionMessage(err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!canDeletePatternRecords) {
      setActionMessage('Only owners and admins can delete historical patterns.');
      return;
    }

    if (selectedPatternIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedPatternIds.length} selected pattern(s)?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/patterns/manage/bulk`, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify({ pattern_ids: selectedPatternIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Bulk delete failed');

      const summary = `Deleted ${data.deleted_count}. Failed ${data.failed_count}.`;
      setActionMessage(summary);
      setSelectedPatternIds([]);
      await refreshAllColumns();
    } catch (err) {
      setActionMessage(err.message);
    }
  };

  const openEditDialog = (pattern) => {
    if (!canEditPatternRecords) {
      setActionMessage('You do not have permission to edit pattern details.');
      return;
    }

    if (!pattern) return;
    setEditingPattern(pattern);
    setEditForm({
      pattern_number: pattern.pattern_number || '',
      garment_type: (pattern.garment_type || 'trouser').toLowerCase(),
      notes: pattern.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingPattern) return;
    if (!canEditPatternRecords) {
      setActionMessage('You do not have permission to edit pattern details.');
      return;
    }

    setIsEditSaving(true);
    try {
      const payload = {
        pattern_number: String(editForm.pattern_number || '').toUpperCase(),
        client_number: String(editingPattern.client_number || '').toUpperCase(),
        client_name: String(editingPattern.client_name || '').trim(),
        garment_type: String(editForm.garment_type || editingPattern.garment_type || 'trouser').toLowerCase(),
        measurements: editingPattern.measurements || {},
        parameters: editingPattern.parameters || {},
        pattern_data: editingPattern.pattern_data || null,
        export_layout: editingPattern.export_layout || null,
        formula_version: editingPattern.formula_version || null,
        geometry_version: editingPattern.geometry_version || null,
        notes: String(editForm.notes || '').trim() || null,
      };

      const response = await fetch(`${API_BASE}/api/patterns/${editingPattern.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update pattern');

      setActionMessage(`Pattern ${data.pattern_number} updated successfully.`);
      setIsEditDialogOpen(false);
      setEditingPattern(null);
      await refreshAllColumns();
    } catch (err) {
      setActionMessage(err.message);
    } finally {
      setIsEditSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background" data-testid="manage-patterns-no-access">
        <div className="text-center space-y-3">
          <h1 className="text-xl font-semibold">Manage Patterns</h1>
          <p className="text-sm text-muted-foreground">Your role does not have access.</p>
          <Button onClick={onBack} data-testid="manage-patterns-back-button">Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-background flex flex-col" data-testid="manage-patterns-page">
      <div className="h-16 border-b px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="manage-patterns-back-button">
            <ArrowLeft size={14} /> Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2"><FolderTree size={18} /> Manage Patterns</h1>
            <p className="text-xs text-muted-foreground">Client-first creation: add client → pick garment → start version draft</p>
          </div>
        </div>
        {canDeletePatternRecords && selectedPatternIds.length > 0 && (
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="manage-patterns-bulk-delete-button">
            <Trash2 size={14} /> Delete Selected ({selectedPatternIds.length})
          </Button>
        )}
      </div>

      {actionMessage && (
        <div className="px-6 py-2 text-sm border-b bg-muted/30" data-testid="manage-patterns-action-message">{actionMessage}</div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 overflow-hidden">
        <section className="border rounded-lg overflow-hidden flex flex-col" data-testid="manage-clients-column">
          <ColumnHeader title="Clients" subtitle={`Total: ${clientTotal}`} />
          <div className="p-3 border-b space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={() => {
                setNewClientError('');
                setIsNewClientDialogOpen(true);
              }}
              disabled={!canCreateOrEditClientRecords}
              data-testid="manage-add-client-button"
            >
              <UserPlus size={14} /> Add New Client
            </Button>
            <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-6 top-1/2 -translate-y-1/2" />
            <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="pl-8" placeholder="Search client" data-testid="manage-clients-search-input" />
            </div>
          </div>
          <ScrollArea className="flex-1">
            {loadingClients ? <div className="p-4 text-sm text-muted-foreground" data-testid="manage-clients-loading">Loading clients...</div> : clients.map((client) => (
              <div
                key={`${client.client_number}-${client.client_name}`}
                className={`w-full px-4 py-3 border-b transition-colors ${normalizeClientKey(selectedClient?.client_number) === normalizeClientKey(client.client_number) ? 'bg-muted/50' : ''}`}
                data-testid={`manage-client-row-${client.client_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    onClick={() => {
                      setSelectedClient({
                        ...client,
                        client_number: String(client.client_number || '').trim(),
                      });
                      setSelectedGarment(null);
                      setPatternPage(1);
                      setGarmentPage(1);
                      setSelectedPatternIds([]);
                    }}
                    className="flex-1 text-left hover:opacity-80"
                    data-testid={`manage-client-item-${client.client_number}`}
                  >
                    <div className="text-sm font-medium">#{client.client_number} · {client.client_name}</div>
                    <div className="text-xs text-muted-foreground">{client.pattern_count} pattern(s)</div>
                  </button>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!canCreateOrEditClientRecords}
                      onClick={(event) => {
                        event.stopPropagation();
                        openClientEditDialog(client);
                      }}
                      data-testid={`manage-client-edit-${client.client_number}`}
                    >
                      <Pencil size={14} className="text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!canDeleteClientRecords || Number(client.pattern_count || 0) > 0}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClientDelete(client);
                      }}
                      data-testid={`manage-client-delete-${client.client_number}`}
                    >
                      <Trash2 size={14} className={Number(client.pattern_count || 0) > 0 ? 'text-muted-foreground' : 'text-red-600'} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {!loadingClients && clients.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground" data-testid="manage-clients-empty-state">No clients found.</div>
            )}
          </ScrollArea>
        </section>

        <section className="border rounded-lg overflow-hidden flex flex-col" data-testid="manage-garments-column">
          <ColumnHeader title="Garments" subtitle={selectedClient ? `Client #${selectedClient.client_number}` : 'Select a client'} />
          <div className="p-3 border-b space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              disabled={!selectedClient || !canCreatePatternDrafts}
              onClick={() => openDraftDialog('new_garment')}
              data-testid="manage-add-garment-button"
            >
              <Sparkles size={14} /> Add New Garment Draft
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              disabled={!selectedClient || !canCreatePatternDrafts}
              onClick={openSourcePatternDuplicateDialog}
              data-testid="manage-duplicate-from-existing-button"
            >
              <FilePlus2 size={14} /> Duplicate From Existing Pattern
            </Button>
            <div className="relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-6 top-1/2 -translate-y-1/2" />
              <Input value={garmentSearch} onChange={(e) => { setGarmentSearch(e.target.value); setGarmentPage(1); }} className="pl-8" placeholder="Search garment" disabled={!selectedClient} data-testid="manage-garments-search-input" />
            </div>
          </div>
          <ScrollArea className="flex-1">
            {loadingGarments ? <div className="p-4 text-sm text-muted-foreground" data-testid="manage-garments-loading">Loading garments...</div> : garments.map((garment) => (
              <button
                key={garment.garment_type}
                onClick={() => {
                  setSelectedGarment(garment);
                  setPatternPage(1);
                  setSelectedPatternIds([]);
                }}
                className={`w-full text-left px-4 py-3 border-b hover:bg-muted/40 transition-colors ${selectedGarment?.garment_type === garment.garment_type ? 'bg-muted/50' : ''}`}
                data-testid={`manage-garment-item-${garment.garment_type}`}
              >
                <div className="text-sm font-medium uppercase">{garment.garment_type}</div>
                <div className="text-xs text-muted-foreground">{garment.pattern_count} pattern(s)</div>
              </button>
            ))}
            {!loadingGarments && garments.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground" data-testid="manage-garments-empty-state">No garments yet for this client.</div>
            )}
          </ScrollArea>
        </section>

        <section className="border rounded-lg overflow-hidden flex flex-col" data-testid="manage-patterns-column">
          <ColumnHeader title="Pattern Versions" subtitle={selectedGarment ? `${selectedGarment.garment_type.toUpperCase()}` : 'Select garment'} />
          <div className="p-3 border-b space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              disabled={!selectedClient || !selectedGarment || !canCreatePatternDrafts}
              onClick={() => openDraftDialog('new_version', { forcedGarment: selectedGarment?.garment_type })}
              data-testid="manage-add-pattern-version-button"
            >
              <FilePlus2 size={14} /> Create New Version Draft
            </Button>
            <div className="relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-6 top-1/2 -translate-y-1/2" />
              <Input value={patternSearch} onChange={(e) => { setPatternSearch(e.target.value); setPatternPage(1); }} className="pl-8" placeholder="Search pattern number" disabled={!selectedClient} data-testid="manage-patterns-search-input" />
            </div>
          </div>
          <ScrollArea className="flex-1">
            {loadingPatterns ? <div className="p-4 text-sm text-muted-foreground" data-testid="manage-patterns-loading">Loading patterns...</div> : patterns.map((pattern) => (
              <div key={pattern.id} className="px-4 py-3 border-b" data-testid={`manage-pattern-item-${pattern.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedPatternIds.includes(pattern.id)}
                      disabled={!canDeletePatternRecords}
                      onCheckedChange={(checked) => {
                        setSelectedPatternIds((prev) => checked === true ? [...prev, pattern.id] : prev.filter((id) => id !== pattern.id));
                      }}
                      data-testid={`manage-pattern-select-${pattern.id}`}
                    />
                    <div>
                      <div className="text-sm font-medium">
                        {pattern.pattern_number}
                        {pattern.edit_locked ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700">Locked</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">{new Date(pattern.created_at).toLocaleString()}</div>
                      <div className="text-[11px] text-muted-foreground" data-testid={`manage-pattern-geometry-${pattern.id}`}>
                        Geometry: {pattern.geometry_version || 'legacy'}
                      </div>
                      {pattern.notes && (
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-2" data-testid={`manage-pattern-notes-${pattern.id}`}>
                          {pattern.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => typeof onOpenPattern === 'function' && onOpenPattern(pattern)}
                    data-testid={`manage-pattern-open-${pattern.id}`}
                  >
                    <FolderOpen size={14} className="text-emerald-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canCreatePatternDrafts}
                    onClick={() => handleDuplicatePattern(pattern)}
                    data-testid={`manage-pattern-revision-${pattern.id}`}
                  >
                    <FilePlus2 size={14} className="text-violet-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canEditPatternRecords}
                    onClick={() => openEditDialog(pattern)}
                    data-testid={`manage-pattern-edit-${pattern.id}`}
                  >
                    <Pencil size={14} className="text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canDeletePatternRecords}
                    onClick={() => handleSingleDelete(pattern.id)}
                    data-testid={`manage-pattern-delete-${pattern.id}`}
                  >
                    <Trash2 size={14} className="text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
            {!loadingPatterns && patterns.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground" data-testid="manage-patterns-empty-state">No pattern versions found.</div>
            )}
          </ScrollArea>
        </section>
      </div>

      <Dialog open={isClientEditDialogOpen} onOpenChange={setIsClientEditDialogOpen}>
        <DialogContent data-testid="manage-client-edit-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manage-client-edit-dialog-title">Edit Client</DialogTitle>
            <DialogDescription data-testid="manage-client-edit-dialog-description">
              Update client number and name. Pattern numbers are auto-renamed safely when possible.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {clientEditError && (
              <div className="text-sm text-red-600" data-testid="manage-client-edit-error-text">{clientEditError}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="manage-client-edit-number">Client Number</Label>
              <Input
                id="manage-client-edit-number"
                value={clientEditForm.client_number}
                onChange={(e) => setClientEditForm((prev) => ({ ...prev, client_number: e.target.value.toUpperCase() }))}
                className="uppercase"
                data-testid="manage-client-edit-number-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manage-client-edit-name">Client Name</Label>
              <Input
                id="manage-client-edit-name"
                value={clientEditForm.client_name}
                onChange={(e) => setClientEditForm((prev) => ({ ...prev, client_name: e.target.value }))}
                data-testid="manage-client-edit-name-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsClientEditDialogOpen(false)}
              data-testid="manage-client-edit-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClientEditSave}
              disabled={isClientEditSaving || !canCreateOrEditClientRecords}
              data-testid="manage-client-edit-save-button"
            >
              {isClientEditSaving ? 'Saving...' : 'Save Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isNewClientDialogOpen} onOpenChange={setIsNewClientDialogOpen}>
        <DialogContent data-testid="manage-new-client-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manage-new-client-dialog-title">Add New Client</DialogTitle>
            <DialogDescription data-testid="manage-new-client-dialog-description">
              Client number must be unique. Existing clients cannot be recreated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {newClientError && (
              <div className="text-sm text-red-600" data-testid="manage-new-client-error-text">{newClientError}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="manage-new-client-number">Client Number</Label>
              <Input
                id="manage-new-client-number"
                value={newClientForm.client_number}
                onChange={(e) => setNewClientForm((prev) => ({ ...prev, client_number: e.target.value.toUpperCase() }))}
                placeholder="1256"
                className="uppercase"
                data-testid="manage-new-client-number-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manage-new-client-name">Client Name</Label>
              <Input
                id="manage-new-client-name"
                value={newClientForm.client_name}
                onChange={(e) => setNewClientForm((prev) => ({ ...prev, client_name: e.target.value }))}
                placeholder="Client full name"
                data-testid="manage-new-client-name-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewClientDialogOpen(false)}
              data-testid="manage-new-client-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateClient}
              disabled={isCreatingClient || !canCreateOrEditClientRecords}
              data-testid="manage-new-client-submit-button"
            >
              {isCreatingClient ? 'Creating...' : 'Create Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDraftDialogOpen} onOpenChange={setIsDraftDialogOpen}>
        <DialogContent data-testid="manage-create-draft-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manage-create-draft-dialog-title">Create Pattern Draft</DialogTitle>
            <DialogDescription data-testid="manage-create-draft-dialog-description">
              {draftMode === 'revision'
                ? 'Create an exact duplicate from the selected version.'
                : draftMode === 'cross_client_duplicate'
                  ? 'Use another client\'s pattern as the exact starting point for this client.'
                : 'Create a new draft version for selected client and garment.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {draftError && (
              <div className="text-sm text-red-600" data-testid="manage-create-draft-error-text">{draftError}</div>
            )}

            {draftForm.garment_type === 'jacket' && !isExactDuplicateDraftMode && (
              <>
                <div className="space-y-1.5" data-testid="manage-create-draft-belly-profile-wrap">
                  <Label>Belly</Label>
                  <Select
                    value={draftForm.bellyProfile || 'normal'}
                    onValueChange={(value) => setDraftForm((prev) => ({ ...prev, bellyProfile: value }))}
                  >
                    <SelectTrigger data-testid="manage-create-draft-belly-profile-select-trigger">
                      <SelectValue placeholder="Select belly profile" />
                    </SelectTrigger>
                    <SelectContent>
                      {JACKET_BELLY_PROFILE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`manage-create-draft-belly-profile-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5" data-testid="manage-create-draft-posture-wrap">
                  <Label>Posture</Label>
                  <Select
                    value={draftForm.posture || 'normal'}
                    onValueChange={(value) => setDraftForm((prev) => ({ ...prev, posture: value }))}
                  >
                    <SelectTrigger data-testid="manage-create-draft-posture-select-trigger">
                      <SelectValue placeholder="Select posture" />
                    </SelectTrigger>
                    <SelectContent>
                      {JACKET_POSTURE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`manage-create-draft-posture-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label>Client</Label>
              <div className="text-sm rounded-md border px-3 py-2 bg-muted/40" data-testid="manage-create-draft-client-text">
                #{selectedClient?.client_number} · {selectedClient?.client_name}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Garment</Label>
              <Select
                value={draftForm.garment_type}
                onValueChange={handleDraftGarmentChange}
                disabled={isExactDuplicateDraftMode || draftMode === 'new_version'}
              >
                <SelectTrigger data-testid="manage-create-draft-garment-select-trigger">
                  <SelectValue placeholder="Select garment" />
                </SelectTrigger>
                <SelectContent>
                  {GARMENT_OPTIONS.map((garment) => (
                    <SelectItem key={garment} value={garment} data-testid={`manage-create-draft-garment-${garment}-option`}>
                      {garment}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isExactDuplicateDraftMode && (
            <div className="space-y-1.5">
              <Label htmlFor="manage-create-draft-version-number">Version Number</Label>
              <Input
                id="manage-create-draft-version-number"
                type="number"
                min="1"
                value={draftForm.version_number}
                onChange={(e) => {
                  const value = e.target.value;
                  const version = Number(value || 1);
                  setDraftForm((prev) => ({
                    ...prev,
                    version_number: value,
                    pattern_number: buildPatternNumber(prev.garment_type, selectedClient?.client_number, Number.isFinite(version) && version > 0 ? version : 1),
                  }));
                }}
                data-testid="manage-create-draft-version-number-input"
              />
            </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="manage-create-draft-pattern-number">Pattern Number</Label>
              <Input
                id="manage-create-draft-pattern-number"
                value={draftForm.pattern_number}
                onChange={(e) => setDraftForm((prev) => ({ ...prev, pattern_number: e.target.value.toUpperCase() }))}
                className="font-mono uppercase"
                data-testid="manage-create-draft-pattern-number-input"
              />
            </div>

            {!isExactDuplicateDraftMode && (
            <div className="space-y-1.5">
              <Label>Geometry Version</Label>
              <Select
                value={draftForm.geometry_version || getGarmentConfig(draftForm.garment_type)?.geometryVersion || ''}
                onValueChange={(value) => setDraftForm((prev) => ({ ...prev, geometry_version: value }))}
              >
                <SelectTrigger data-testid="manage-create-draft-geometry-version-select-trigger">
                  <SelectValue placeholder="Select geometry version" />
                </SelectTrigger>
                <SelectContent>
                  {[...getSupportedGeometryVersions(draftForm.garment_type)].map((version) => (
                    <SelectItem
                      key={version}
                      value={version}
                      data-testid={`manage-create-draft-geometry-version-option-${version}`}
                    >
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {draftSourcePattern && (
                <p className="text-[11px] text-muted-foreground" data-testid="manage-create-draft-geometry-inherit-helper-text">
                  Source geometry: {resolveGeometryVersion(draftForm.garment_type, draftSourcePattern)} (inherited by default)
                </p>
              )}
            </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="manage-create-draft-notes-input">Notes</Label>
              <Textarea
                id="manage-create-draft-notes-input"
                value={draftForm.notes}
                onChange={(e) => setDraftForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional fit notes, cutter remarks, or context for this duplicate..."
                rows={4}
                data-testid="manage-create-draft-notes-input"
              />
            </div>

            {draftSourcePattern && (
              <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground" data-testid="manage-create-draft-source-pattern-text">
                Source pattern: {draftSourcePattern.pattern_number} · #{draftSourcePattern.client_number} · {draftSourcePattern.client_name}
              </div>
            )}

            {isDraftLoadingVersion && (
              <div className="text-xs text-muted-foreground" data-testid="manage-create-draft-loading-version-text">
                Resolving next available version...
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDraftDialogOpen(false)}
              data-testid="manage-create-draft-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDraftSubmit}
              disabled={isDraftSubmitting || isDraftLoadingVersion || !canCreatePatternDrafts}
              data-testid="manage-create-draft-submit-button"
            >
              {isDraftSubmitting ? 'Opening...' : (isExactDuplicateDraftMode ? 'Create Duplicate' : 'Open Draft')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSourcePatternDialogOpen} onOpenChange={setIsSourcePatternDialogOpen}>
        <DialogContent data-testid="manage-source-pattern-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manage-source-pattern-picker-title">Duplicate From Existing Pattern</DialogTitle>
            <DialogDescription data-testid="manage-source-pattern-picker-description">
              Choose a source pattern from another client, then create an exact starting copy for the selected client.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {sourcePatternDialogError && (
              <div className="text-sm text-red-600" data-testid="manage-source-pattern-picker-error-text">{sourcePatternDialogError}</div>
            )}

            <div className="space-y-1.5">
              <Label>Target Client</Label>
              <div className="text-sm rounded-md border px-3 py-2 bg-muted/40" data-testid="manage-source-pattern-picker-target-client-text">
                #{selectedClient?.client_number} · {selectedClient?.client_name}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manage-source-pattern-picker-garment-select-trigger">Target Garment</Label>
              <Select
                value={sourcePatternTargetGarment}
                onValueChange={(value) => {
                  setSourcePatternTargetGarment(value);
                  setSelectedSourcePatternForDuplicate(null);
                }}
              >
                <SelectTrigger id="manage-source-pattern-picker-garment-select-trigger" data-testid="manage-source-pattern-picker-garment-select-trigger">
                  <SelectValue placeholder="Select target garment" />
                </SelectTrigger>
                <SelectContent>
                  {GARMENT_OPTIONS.map((garment) => (
                    <SelectItem key={garment} value={garment} data-testid={`manage-source-pattern-picker-garment-option-${garment}`}>
                      {garment}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manage-source-pattern-picker-search-input">Search Source Pattern</Label>
              <Input
                id="manage-source-pattern-picker-search-input"
                value={sourcePatternSearchText}
                onChange={(event) => setSourcePatternSearchText(event.target.value)}
                placeholder="Search by pattern number, client number, or client name"
                data-testid="manage-source-pattern-picker-search-input"
              />
            </div>

            <div className="rounded-md border max-h-[320px] overflow-y-auto" data-testid="manage-source-pattern-picker-results-wrap">
              {isSourcePatternLoading ? (
                <div className="p-3 text-sm text-muted-foreground" data-testid="manage-source-pattern-picker-loading-text">Loading source patterns...</div>
              ) : filteredSourcePatternOptions.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground" data-testid="manage-source-pattern-picker-empty-text">No matching source patterns found.</div>
              ) : filteredSourcePatternOptions.map((pattern) => {
                const isSelected = selectedSourcePatternForDuplicate?.id === pattern.id;
                return (
                  <button
                    key={pattern.id}
                    type="button"
                    onClick={() => {
                      setSelectedSourcePatternForDuplicate(pattern);
                      setSourcePatternDialogError('');
                    }}
                    className={`w-full text-left px-3 py-3 border-b last:border-b-0 transition-colors ${isSelected ? 'bg-muted/60' : 'hover:bg-muted/30'}`}
                    data-testid={`manage-source-pattern-picker-option-${pattern.id}`}
                  >
                    <div className="text-sm font-medium" data-testid={`manage-source-pattern-picker-option-pattern-number-${pattern.id}`}>{pattern.pattern_number}</div>
                    <div className="text-xs text-muted-foreground" data-testid={`manage-source-pattern-picker-option-client-${pattern.id}`}>
                      #{pattern.client_number} · {pattern.client_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground" data-testid={`manage-source-pattern-picker-option-geometry-${pattern.id}`}>
                      Geometry: {pattern.geometry_version || 'legacy'}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedSourcePatternForDuplicate && (
              <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground" data-testid="manage-source-pattern-picker-selected-source-text">
                Selected source: {selectedSourcePatternForDuplicate.pattern_number} · #{selectedSourcePatternForDuplicate.client_number} · {selectedSourcePatternForDuplicate.client_name}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSourcePatternDialogOpen(false)}
              data-testid="manage-source-pattern-picker-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSourcePatternDuplicateContinue}
              disabled={!selectedSourcePatternForDuplicate}
              data-testid="manage-source-pattern-picker-continue-button"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent data-testid="manage-pattern-edit-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manage-pattern-edit-dialog-title">Edit Pattern</DialogTitle>
            <DialogDescription data-testid="manage-pattern-edit-dialog-description">
              Update pattern details only.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="manage-edit-pattern-number">Pattern Number</Label>
              <Input
                id="manage-edit-pattern-number"
                value={editForm.pattern_number}
                onChange={(e) => setEditForm((prev) => ({ ...prev, pattern_number: e.target.value }))}
                data-testid="manage-pattern-edit-pattern-number-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Garment Type</Label>
              <Select
                value={editForm.garment_type || 'trouser'}
                onValueChange={(value) => setEditForm((prev) => ({ ...prev, garment_type: value }))}
              >
                <SelectTrigger data-testid="manage-pattern-edit-garment-type-select-trigger">
                  <SelectValue placeholder="Select garment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trouser" data-testid="manage-pattern-edit-garment-type-trouser-option">Trouser</SelectItem>
                  <SelectItem value="shirt" data-testid="manage-pattern-edit-garment-type-shirt-option">Shirt</SelectItem>
                  <SelectItem value="jacket" data-testid="manage-pattern-edit-garment-type-jacket-option">Jacket</SelectItem>
                  <SelectItem value="waistcoat" data-testid="manage-pattern-edit-garment-type-waistcoat-option">Waistcoat</SelectItem>
                  <SelectItem value="overcoat" data-testid="manage-pattern-edit-garment-type-overcoat-option">Overcoat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manage-pattern-edit-notes">Notes</Label>
              <Textarea
                id="manage-pattern-edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional fit notes, cutter remarks, version context..."
                rows={4}
                data-testid="manage-pattern-edit-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              data-testid="manage-pattern-edit-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={isEditSaving || !canEditPatternRecords}
              data-testid="manage-pattern-edit-save-button"
            >
              {isEditSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
